import { useState, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import api from '../../lib/axios'
import { PageHeader, Card, Spinner, Button, StatusBadge } from '../../components/ui'
import RepositoryAutomationPanel from '../../components/repository/RepositoryAutomationPanel'
import toast from 'react-hot-toast'

export default function ProjectDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [project, setProject]       = useState(null)
  const [loading, setLoading]       = useState(true)
  const [autoRepo, setAutoRepo]     = useState(null)  // repo whose automation panel is open
  const [syncingId, setSyncingId]   = useState(null)

  useEffect(() => {
    api.get(`/projects/${id}`)
      .then(r => setProject(r.data?.data ?? r.data))
      .finally(() => setLoading(false))
  }, [id])

  const syncRepo = async (repo) => {
    setSyncingId(repo.id)
    try {
      await api.post(`/repositories/${repo.id}/sync`)
      toast.success(`${repo.full_name} synced.`)
      const r = await api.get(`/projects/${id}`)
      setProject(r.data?.data ?? r.data)
    } catch { toast.error('Sync failed.') }
    finally { setSyncingId(null) }
  }

  if (loading) return <div className="flex items-center justify-center h-64"><Spinner size="lg" /></div>
  if (!project) return <div className="p-8 text-white/50">Project not found.</div>

  return (
    <div className="pb-10 animate-fade-in" style={{ background: 'linear-gradient(180deg,#0d0521 0%,#07020f 100%)', minHeight: '100%' }}>
      <PageHeader title={project.name} subtitle={project.description}>
        <Link to="/dashboard/projects" className="text-xs text-white/40 hover:text-white transition-colors">← Projects</Link>
      </PageHeader>

      <div className="px-8 space-y-6">

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4">
          {[
            { label: 'Documents',      count: project.documents_count,      icon: '📄', to: '/dashboard/documents' },
            { label: 'Test Plans',     count: project.test_plans_count,     icon: '🧪', to: '/dashboard/test-plans' },
            { label: 'Specifications', count: project.specifications_count, icon: '◈',  to: '/dashboard/specifications' },
          ].map(s => (
            <Link key={s.label} to={s.to} className="glass-card-hover p-5 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl"
                style={{ background: 'rgba(139,63,255,0.15)', border: '1px solid rgba(139,63,255,0.2)' }}>
                {s.icon}
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{s.count ?? 0}</p>
                <p className="text-xs text-white/40">{s.label}</p>
              </div>
            </Link>
          ))}
        </div>

        {/* Repositories */}
        <Card className="p-0 overflow-hidden">
          <div className="px-5 py-4 border-b border-white/6 flex items-center justify-between">
            <h2 className="font-bold text-white text-sm">Repositories</h2>
            <span className="text-xs text-white/30">{project.repositories?.length ?? 0} connected</span>
          </div>
          {!project.repositories?.length ? (
            <div className="px-5 py-8 text-center text-sm text-white/40">
              No repositories connected yet.
            </div>
          ) : (
            <div className="divide-y divide-white/5">
              {project.repositories.map(repo => (
                <div key={repo.id}>
                  <div className="flex items-center gap-3 px-5 py-4">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center text-base"
                      style={{ background: 'rgba(59,130,246,0.12)' }}>
                      {repo.provider === 'github' ? '⌥' : repo.provider === 'gitlab' ? '🦊' : '🪣'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-white truncate">{repo.full_name}</p>
                      <p className="text-[11px] text-white/35 capitalize">
                        {repo.provider} · {repo.language ?? 'unknown'} · {repo.default_branch}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {repo.webhook_enabled && (
                        <span className="text-[9px] font-bold uppercase text-emerald-400 bg-emerald-500/10 px-1.5 py-0.5 rounded-full border border-emerald-500/20">WEBHOOK</span>
                      )}
                      <button onClick={() => syncRepo(repo)} disabled={syncingId === repo.id}
                        className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-white/5 border border-white/10 text-white/50 text-xs font-medium hover:bg-white/10 transition-all disabled:opacity-50">
                        {syncingId === repo.id ? '⟳ Syncing…' : '⟳ Sync'}
                      </button>
                      <button
                        onClick={() => setAutoRepo(autoRepo?.id === repo.id ? null : repo)}
                        className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg border text-xs font-medium transition-all ${
                          autoRepo?.id === repo.id
                            ? 'bg-purple-500/15 border-purple-500/30 text-purple-300'
                            : 'bg-white/5 border-white/10 text-white/50 hover:bg-white/10'}`}>
                        ⚙ Automation
                      </button>
                    </div>
                  </div>

                  {/* Automation panel — inline below the repo row */}
                  {autoRepo?.id === repo.id && (
                    <div className="border-t border-white/6 px-5 py-5 bg-white/[0.01]">
                      <RepositoryAutomationPanel
                        repositoryId={repo.id}
                        provider={repo.provider}
                        fullName={repo.full_name}
                        defaultBranch={repo.default_branch ?? 'main'}
                        onClose={() => setAutoRepo(null)}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  )
}
